import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, X, Bell, ChevronDown, LogOut, User, List, HelpCircle, Pencil, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_cca8665b-5293-4dd5-89e0-2957c9d7e36c/artifacts/zjtkx4ss_LOGO-MAYAPLUS.png";

const NAV_ITEMS = [
  { label: 'Accueil', path: '/' },
  { label: 'Séries', path: '/browse?type=series' },
  { label: 'Films', path: '/browse?type=movie' },
  { label: 'Jeux', path: '/browse?type=game' },
  { label: 'Nouveautés', path: '/browse?category=nouveautes' },
  { label: 'Ma liste', path: '/my-list' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const { user, logout, activeProfile, setActiveProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Fetch profiles when menu opens
  useEffect(() => {
    if (!showMenu) return;
    axios.get(`${API}/profiles`, { withCredentials: true })
      .then(r => setProfiles(r.data))
      .catch(() => {});
  }, [showMenu]);

  // Notifications: fetch on mount + every 2 min
  useEffect(() => {
    const load = () => axios.get(`${API}/notifications`, { params: { platform: 'web' }, withCredentials: true })
      .then(r => setNotifs(r.data || [])).catch(() => {});
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, []);

  const unreadCount = notifs.filter(n => !n.read).length;

  const openNotifs = () => {
    setShowNotifs(v => !v);
    if (!showNotifs && unreadCount > 0) {
      axios.post(`${API}/notifications/mark-read`, {}, { withCredentials: true })
        .then(() => setNotifs(ns => ns.map(n => ({ ...n, read: true }))))
        .catch(() => {});
    }
  };

  const openNotifContent = (n) => {
    setShowNotifs(false);
    if (n.content_id) navigate(`/content/${n.content_id}`);
  };

  const timeAgo = (iso) => {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 3600) return `il y a ${Math.max(1, Math.floor(diff / 60))} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
    return `il y a ${Math.floor(diff / 86400)} j`;
  };

  const handleSearch = useCallback((e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
    }
  }, [searchQuery, navigate]);

  const switchProfile = async (profile) => {
    setShowMenu(false);
    await setActiveProfile(profile);
    navigate('/');
  };

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-[#11041F]/95 backdrop-blur-xl shadow-2xl shadow-black/40' : 'bg-gradient-to-b from-black/70 to-transparent'
      }`}
    >
      <div className="flex items-center justify-between px-4 md:px-12 h-16 md:h-[68px]">
        {/* Left: Logo + Nav */}
        <div className="flex items-center gap-8">
          <Link to="/" data-testid="header-logo">
            <img src={LOGO_URL} alt="Maya+" className="h-7 md:h-9" />
          </Link>
          {user?.is_test_account && (() => {
            const exp = user.test_expires_at ? new Date(user.test_expires_at) : null;
            const daysLeft = exp ? Math.ceil((exp.getTime() - Date.now()) / 86400000) : null;
            const soon = daysLeft !== null && daysLeft <= 3;
            return (
              <span data-testid={soon ? 'test-account-expiry-warning' : 'test-account-badge'}
                title={soon ? `Votre compte test expire le ${exp.toLocaleDateString('fr-FR')}` : 'Compte de test — accès gratuit, aucune facturation'}
                className={`-ml-4 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border whitespace-nowrap ${
                  soon ? 'bg-amber-500/15 text-amber-400 border-amber-500/40' : 'bg-[#E9641A]/15 text-[#FE975E] border-[#E9641A]/30'
                }`}>
                Compte test{soon && (daysLeft <= 0 ? ' · expire aujourd\'hui' : ` · expire dans ${daysLeft} j`)}
              </span>
            );
          })()}

          <nav className="hidden lg:flex items-center gap-1.5">
            {NAV_ITEMS.map(item => {
              const active = (location.pathname + location.search) === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={`text-[16px] px-4 py-1.5 rounded-full transition-all duration-200 ${
                    active
                      ? 'bg-[#6D28D9] text-white font-semibold shadow-lg shadow-[#6D28D9]/40'
                      : 'text-white/60 font-medium hover:text-white hover:bg-white/10'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Right: Search + Notifications + User — icons 20% bigger */}
        <div className="flex items-center gap-5">
          {/* Search */}
          {searchOpen ? (
            <form onSubmit={handleSearch} className="flex items-center bg-[#11041F]/90 border border-white/20 rounded overflow-hidden animate-fadeIn">
              <Search size={19} className="text-white/40 ml-3" />
              <input
                data-testid="search-input"
                autoFocus
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Titres, personnes, genres"
                className="bg-transparent text-white text-sm px-3 py-2 w-44 md:w-64 outline-none placeholder-white/30"
              />
              <button type="button" onClick={() => { setSearchOpen(false); setSearchQuery(''); }} className="px-2 text-white/40 hover:text-white">
                <X size={19} />
              </button>
            </form>
          ) : (
            <button
              data-testid="search-toggle-btn"
              onClick={() => setSearchOpen(true)}
              className="text-white/70 hover:text-white transition-colors"
            >
              <Search size={24} />
            </button>
          )}

          {/* Notifications bell */}
          <div className="relative hidden md:block">
            <button onClick={openNotifs} data-testid="notif-bell-btn"
              className="relative text-white/70 hover:text-white transition-colors block">
              <Bell size={24} />
              {unreadCount > 0 && (
                <span data-testid="notif-badge"
                  className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E9641A] text-white text-[11px] font-bold flex items-center justify-center leading-none">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifs && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowNotifs(false)} />
                <div className="absolute right-3 top-[38px] z-50 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[8px] border-b-white/20" />
                <div data-testid="notif-dropdown"
                  className="absolute right-0 top-[46px] w-[380px] max-h-[70vh] overflow-y-auto bg-[#0d0520]/95 backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl shadow-black/60 animate-scaleIn z-50">
                  <div className="px-4 py-3 border-b border-white/10 text-sm font-semibold text-white">Notifications</div>
                  {notifs.length === 0 ? (
                    <p className="text-center text-sm text-white/40 py-10" data-testid="notif-empty">Aucune notification pour le moment.</p>
                  ) : notifs.map(n => (
                    <button key={n.id} onClick={() => openNotifContent(n)} data-testid={`notif-row-${n.id}`}
                      className={`flex items-start gap-3 px-4 py-3 w-full text-left border-b border-white/5 transition-colors ${n.content_id ? 'hover:bg-white/5 cursor-pointer' : 'cursor-default'}`}>
                      <span className="w-9 h-9 rounded-full bg-[#E9641A]/15 flex items-center justify-center flex-none mt-0.5">
                        <Bell size={15} className="text-[#E9641A]" />
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-[13px] font-semibold text-white leading-snug">{n.title}</span>
                        <span className="block text-xs text-white/55 leading-snug mt-0.5">{n.message}</span>
                        <span className="block text-[11px] text-white/35 mt-1">
                          {timeAgo(n.created_at)}
                          {n.content_id && <span className="text-[#75D4D5] ml-2">Voir le contenu →</span>}
                        </span>
                      </span>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-[#E9641A] flex-none mt-2" />}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* User Menu */}
          <div className="relative">
            <button
              data-testid="user-menu-btn"
              onClick={() => setShowMenu(!showMenu)}
              className="flex items-center gap-2 group"
            >
              <div className="w-9 h-9 rounded overflow-hidden border-2 border-white/10 group-hover:border-white/40 transition-colors">
                <img
                  src={activeProfile?.avatar_url || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face'}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                />
              </div>
              <ChevronDown size={16} className={`text-white/50 transition-transform duration-200 ${showMenu ? 'rotate-180' : ''}`} />
            </button>

            {showMenu && (
              <>
                {/* Backdrop to close menu on click outside */}
                <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />

                {/* Arrow pointer */}
                <div className="absolute right-3 top-[44px] z-50 w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[8px] border-b-white/20" />

                <div
                  className="absolute right-0 top-[52px] w-64 bg-[#0d0520]/95 backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl shadow-black/60 overflow-hidden animate-scaleIn z-50"
                  data-testid="user-dropdown"
                >
                  {/* Profiles list */}
                  <div className="py-2">
                    {profiles.filter(p => p.id !== activeProfile?.id).map(profile => (
                      <button
                        key={profile.id}
                        onClick={() => switchProfile(profile)}
                        data-testid={`profile-switch-${profile.id}`}
                        className="flex items-center gap-3 px-4 py-2.5 w-full text-left hover:bg-white/5 transition-colors"
                      >
                        <div className="w-8 h-8 rounded overflow-hidden flex-none bg-[#1a0e2e]">
                          <img
                            src={profile.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.name)}&background=E9641A&color=fff&size=64`}
                            alt={profile.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span className="text-sm text-white/70">{profile.name}</span>
                      </button>
                    ))}
                  </div>

                  <div className="border-t border-white/10">
                    <button
                      onClick={() => { setShowMenu(false); setActiveProfile(null); navigate('/'); }}
                      data-testid="dropdown-switch-profile"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors w-full"
                    >
                      <Users size={16} />
                      Changer de profil
                    </button>
                    <Link
                      to="/account/settings"
                      data-testid="dropdown-manage-profiles"
                      onClick={() => setShowMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors"
                    >
                      <Pencil size={16} />
                      Gérer les profils
                    </Link>
                    <Link
                      to="/my-list"
                      data-testid="dropdown-mylist"
                      onClick={() => setShowMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors"
                    >
                      <List size={16} />
                      Ma liste
                    </Link>
                    <Link
                      to="/account"
                      data-testid="dropdown-account"
                      onClick={() => setShowMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors"
                    >
                      <User size={16} />
                      Mon compte
                    </Link>
                    <Link
                      to="/help"
                      data-testid="dropdown-help"
                      onClick={() => setShowMenu(false)}
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors"
                    >
                      <HelpCircle size={16} />
                      Centre d'aide
                    </Link>
                  </div>

                  <div className="border-t border-white/10">
                    <button
                      data-testid="logout-btn"
                      onClick={() => { logout(); setShowMenu(false); }}
                      className="flex items-center justify-center gap-2 px-4 py-3 text-sm text-white/60 hover:text-white hover:bg-white/5 transition-colors w-full"
                    >
                      <LogOut size={16} />
                      Se déconnecter
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
